/**
 * Event Outreach Studio — AI-drafts a 3-message cadence × selected-channels
 * campaign for a specific contact × event, pre- or post-event.
 *
 * Launched from the Contact Detail page (Event Outreach button). The final
 * step bulk-creates one activity (type='task', task_kind='outreach_send')
 * per message so the sequence shows up in the contact's Activities feed
 * AND the global Tasks page. Sending is outside this PR — reps copy the
 * body and hit Mark as sent on the task card; responses can be pasted
 * back and "Rework next message" regenerates the next one with the
 * full conversation chain.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
    ArrowLeft, Sparkles, Check, Loader2, Calendar, Mail, MessageCircle,
    Linkedin, Instagram, Facebook, Twitter, Phone, FileText, Edit3, Copy,
    ChevronDown, ChevronUp, Save, X as XIcon, Mic, MicOff,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
    { key: 'instagram_dm', label: 'Instagram DM', Icon: Instagram, max: 1000 },
    { key: 'facebook_dm',  label: 'Facebook DM',  Icon: Facebook,  max: 1000 },
    { key: 'linkedin',     label: 'LinkedIn',     Icon: Linkedin,  max: 2000 },
    { key: 'whatsapp',     label: 'WhatsApp',     Icon: MessageCircle, max: 1000 },
    { key: 'x',            label: 'X (Twitter)',  Icon: Twitter,   max: 280 },
    { key: 'email',        label: 'Email',        Icon: Mail,      max: 4000 },
];

const STEPS = ['Pick event', 'Add context', 'Review & personalise', 'Create send tasks'];

/**
 * useDictation — browser-native Web Speech API wrapper tuned for
 * appending to a free-text input (not "fill this form field").
 *
 *   supported          — Chrome/Edge/Safari have it, Firefox doesn't.
 *                        Consumers should hide the mic button when false.
 *   recording          — true while mic is capturing.
 *   start(onFinal)     — begins a session. `onFinal(text)` fires every
 *                        time the recognizer finalises a chunk (not one
 *                        big blob at the end) so long dictation feels
 *                        responsive and partial text is never lost on
 *                        accidental stops.
 *   stop()             — ends the session cleanly.
 *
 * Errors + permission denials are logged + surfaced as a toast, not
 * thrown — dictation is a progressive-enhancement feature, not a
 * critical path.
 */
function useDictation() {
    const [recording, setRecording] = useState(false);
    const recognitionRef = useRef(null);
    const onFinalRef = useRef(null);

    const Ctor = typeof window !== 'undefined'
        ? (window.SpeechRecognition || window.webkitSpeechRecognition)
        : null;
    const supported = Boolean(Ctor);

    const stop = useCallback(() => {
        const rec = recognitionRef.current;
        if (rec) {
            try { rec.stop(); } catch { /* noop */ }
        }
        setRecording(false);
    }, []);

    const start = useCallback((onFinal) => {
        if (!Ctor) { toast.error('Voice input not supported in this browser'); return; }
        onFinalRef.current = onFinal;
        const rec = new Ctor();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

        rec.onresult = (ev) => {
            let finalChunk = '';
            for (let i = ev.resultIndex; i < ev.results.length; i++) {
                const r = ev.results[i];
                if (r.isFinal) finalChunk += r[0].transcript;
            }
            if (finalChunk && onFinalRef.current) {
                onFinalRef.current(finalChunk.trim());
            }
        };
        rec.onerror = (ev) => {
            // eslint-disable-next-line no-console
            console.warn('[dictation] error:', ev.error);
            if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
                toast.error('Microphone permission denied');
            }
        };
        rec.onend = () => {
            recognitionRef.current = null;
            setRecording(false);
        };
        recognitionRef.current = rec;
        try {
            rec.start();
            setRecording(true);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[dictation] start failed:', err);
            recognitionRef.current = null;
            setRecording(false);
        }
    }, [Ctor]);

    // Cleanup on unmount.
    useEffect(() => () => {
        const rec = recognitionRef.current;
        if (rec) {
            try { rec.abort(); } catch { /* noop */ }
        }
    }, []);

    return { supported, recording, start, stop };
}

function ProgressStepper({ step }) {
    return (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {STEPS.map((label, i) => {
                const n = i + 1;
                const active = step === n;
                const done = step > n;
                return (
                    <div key={label} className="flex items-center gap-2 flex-shrink-0">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                            done ? 'bg-emerald-500 text-white'
                                : active ? 'bg-violet-600 text-white'
                                : 'bg-stone-200 text-stone-500'
                        }`}>
                            {done ? <Check className="w-3.5 h-3.5" /> : n}
                        </div>
                        <span className={`text-xs ${active ? 'font-semibold text-stone-800' : 'text-stone-500'}`}>{label}</span>
                        {i < STEPS.length - 1 && <div className="w-10 h-px bg-stone-200 mx-1" />}
                    </div>
                );
            })}
        </div>
    );
}

function ModeToggle({ mode, onChange }) {
    return (
        <div className="grid grid-cols-2 gap-2 mt-4">
            {[
                { key: 'pre',  title: 'Pre-event',  sub: 'Drive attendance · warm up before meeting' },
                { key: 'post', title: 'Post-event', sub: 'Follow up after meeting · recap + next step' },
            ].map(m => {
                const active = mode === m.key;
                return (
                    <button
                        key={m.key}
                        onClick={() => onChange(m.key)}
                        className={`relative text-left p-4 rounded-xl border transition-all ${
                            active
                                ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                                : 'bg-white border-stone-200 text-stone-700 hover:border-stone-300'
                        }`}
                    >
                        <div className="flex items-start gap-2">
                            <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-semibold text-sm">{m.title}</div>
                                <div className={`text-xs mt-0.5 ${active ? 'text-violet-100' : 'text-stone-500'}`}>{m.sub}</div>
                            </div>
                        </div>
                        {active && <Check className="w-4 h-4 absolute right-3 top-3" />}
                    </button>
                );
            })}
        </div>
    );
}

function EventCard({ event, selected, onSelect }) {
    const status = (event.attendee_status || '').toLowerCase();
    const attended = status === 'attended';
    const invitable = !event.attendee_status;
    // invitable → contact isn't on the attendee list yet. Pre-mode
    // outreach will auto-register them on session create.
    let badge;
    if (attended) badge = { text: 'Attended', className: 'bg-emerald-100 text-emerald-700' };
    else if (status) badge = { text: status, className: 'bg-stone-100 text-stone-600' };
    else badge = { text: 'Invite', className: 'bg-violet-100 text-violet-700' };

    return (
        <button
            onClick={() => onSelect(event.event_id)}
            className={`text-left p-3 rounded-lg border transition-all w-full ${
                selected
                    ? 'border-violet-500 bg-violet-50/40 ring-2 ring-violet-200'
                    : 'border-stone-200 bg-white hover:border-stone-300'
            }`}
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-stone-400 mb-1">
                <span>{event.event_number || `EVT-${(event.event_id || '').slice(0, 4)}`}</span>
                <span className={`px-1.5 py-0.5 rounded capitalize text-[10px] ${badge.className}`}>
                    {attended && <Check className="w-3 h-3 inline -mt-0.5 mr-0.5" />}
                    {badge.text}
                </span>
            </div>
            <div className="text-sm font-semibold text-stone-800 truncate">{event.name}</div>
            <div className="text-xs text-stone-500 mt-0.5">{event.date || ''}</div>
            {(event.venue_name || event.city) && (
                <div className="text-xs text-stone-400 mt-0.5 truncate">
                    {[event.venue_name, event.city].filter(Boolean).join(' · ')}
                </div>
            )}
            {invitable && (
                <div className="text-[10px] text-violet-600 mt-1 italic">
                    Contact will be auto-registered on generate
                </div>
            )}
        </button>
    );
}

function ChannelToggle({ channel, checked, onChange }) {
    const { Icon, label } = channel;
    return (
        <button
            type="button"
            onClick={() => onChange(channel.key, !checked)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                checked
                    ? 'border-violet-500 bg-violet-50 text-violet-700'
                    : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
            }`}
        >
            <span className={`w-4 h-4 rounded border flex items-center justify-center ${
                checked ? 'bg-violet-600 border-violet-600 text-white' : 'border-stone-300'
            }`}>
                {checked && <Check className="w-3 h-3" />}
            </span>
            <Icon className="w-3.5 h-3.5" />
            {label}
        </button>
    );
}

function MessageEditor({ message, onSave, onCancel, saving }) {
    const [body, setBody] = useState(message.body || '');
    const [subject, setSubject] = useState(message.subject || '');
    const isEmail = message.channel === 'email';
    return (
        <div className="space-y-2">
            {isEmail && (
                <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Subject"
                    className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
            )}
            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={7}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded focus:outline-none focus:ring-2 focus:ring-violet-300 resize-vertical"
            />
            <div className="flex items-center gap-2">
                <button
                    onClick={() => onSave({ body, subject: isEmail ? subject : null })}
                    disabled={saving}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded hover:bg-stone-800 disabled:opacity-60"
                >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Save
                </button>
                <button onClick={onCancel} className="text-xs text-stone-500 hover:text-stone-800">Cancel</button>
            </div>
        </div>
    );
}

function MessageRow({ message, session, onChange }) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const char = message.char_count || (message.body || '').length;
    const isEmail = message.channel === 'email';
    const sent = Boolean(message.sent_at);

    const channel = CHANNELS.find(c => c.key === message.channel);

    const save = async (patch) => {
        setSaving(true);
        try {
            const token = localStorage.getItem('crm_token');
            const res = await axios.patch(
                `${API}/event-outreach/${session.session_id}/messages/${message.message_id}`,
                patch,
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            );
            onChange(res.data.session);
            toast.success('Saved');
            setEditing(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally { setSaving(false); }
    };

    const copy = () => {
        const prefix = isEmail && message.subject ? `Subject: ${message.subject}\n\n` : '';
        navigator.clipboard?.writeText(prefix + (message.body || ''));
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
    };

    return (
        <div className="p-4 border border-stone-200 rounded-lg bg-white">
            <div className="flex items-center gap-2 text-[11px] text-stone-500 mb-2 flex-wrap">
                <span className="font-medium text-stone-700">{message.day_label}</span>
                <span>·</span>
                <span className="text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">{message.purpose}</span>
                <span className="ml-auto">{char} chars · max {channel?.max || '—'}</span>
                {sent && <span className="text-emerald-600 font-medium ml-2">Sent ✓</span>}
            </div>
            {isEmail && !editing && (
                <div className="text-xs text-stone-500 mb-1.5">
                    <span className="font-semibold text-stone-600">Subject:</span> {message.subject || <em className="text-stone-400">(none)</em>}
                </div>
            )}
            {editing ? (
                <MessageEditor
                    message={message}
                    saving={saving}
                    onCancel={() => setEditing(false)}
                    onSave={save}
                />
            ) : (
                <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">
                    {message.body || <em className="text-stone-400">(empty)</em>}
                </p>
            )}
            {!editing && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-100 flex-wrap">
                    <button
                        onClick={copy}
                        className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900 px-2 py-1 rounded hover:bg-stone-50"
                    >
                        <Copy className="w-3 h-3" /> {copied ? 'Copied' : 'Copy'}
                    </button>
                    <button
                        onClick={() => setEditing(true)}
                        className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900 px-2 py-1 rounded hover:bg-stone-50"
                    >
                        <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    {message.reworked_at && (
                        <span className="text-[10px] text-stone-400 italic ml-auto">Reworked with response</span>
                    )}
                </div>
            )}
        </div>
    );
}

export default function EventOutreachStudio() {
    const { contactId } = useParams();
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();

    const [contact, setContact] = useState(null);
    const [mode, setMode] = useState('post');
    const [step, setStep] = useState(1);

    // Step 1
    const [events, setEvents] = useState([]);
    const [eventId, setEventId] = useState('');
    const [loadingEvents, setLoadingEvents] = useState(false);

    // Step 2
    const [contextText, setContextText] = useState('');
    const [include, setInclude] = useState({ notes: true, disc: true, voice: true });
    const [channels, setChannels] = useState({ email: true, linkedin: true });
    // eventLinks: [{key, label, url}] — resolved from the selected event.
    // linkInclusion: { link_key: [bool, bool, bool] } — the rep's pick per sequence_index.
    const [eventLinks, setEventLinks] = useState([]);
    const [linkInclusion, setLinkInclusion] = useState({});
    const dictation = useDictation();
    const startDictation = useCallback(() => {
        dictation.start((chunk) => {
            setContextText(prev => {
                const needsSpace = prev.length > 0 && !/\s$/.test(prev);
                return prev + (needsSpace ? ' ' : '') + chunk;
            });
        });
    }, [dictation]);

    // Step 3
    const [session, setSession] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [activeChannel, setActiveChannel] = useState(null);

    // Step 4
    const [creatingTasks, setCreatingTasks] = useState(false);

    // Load contact for header
    useEffect(() => {
        axios.get(`${API}/contacts/${contactId}`, { headers: getAuthHeader() })
            .then(r => setContact(r.data))
            .catch(() => toast.error('Contact not found'));
    }, [contactId, getAuthHeader]);

    // Load eligible events whenever mode changes
    const loadEvents = useCallback(async () => {
        setLoadingEvents(true);
        try {
            const r = await axios.get(
                `${API}/event-outreach/contacts/${contactId}/events?mode=${mode}`,
                { headers: getAuthHeader() },
            );
            setEvents(r.data?.events || []);
        } catch (err) {
            toast.error('Failed to load events');
        } finally {
            setLoadingEvents(false);
        }
    }, [contactId, mode, getAuthHeader]);

    useEffect(() => { loadEvents(); }, [loadEvents]);

    // Pull available links for the selected event. Default inclusion on the
    // first load: link goes into messages 2 + 3, not message 1 (which stays a
    // Connect-only message per the cadence contract).
    useEffect(() => {
        if (!eventId) { setEventLinks([]); setLinkInclusion({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/event-outreach/events/${eventId}/links`,
                    { headers: getAuthHeader() },
                );
                if (cancelled) return;
                const links = r.data?.links || [];
                setEventLinks(links);
                setLinkInclusion(prev => {
                    const next = { ...prev };
                    for (const l of links) {
                        if (!next[l.key]) next[l.key] = [false, true, true];
                    }
                    // Drop keys for links that no longer exist on the event.
                    for (const k of Object.keys(next)) {
                        if (!links.find(l => l.key === k)) delete next[k];
                    }
                    return next;
                });
            } catch {
                if (!cancelled) { setEventLinks([]); setLinkInclusion({}); }
            }
        })();
        return () => { cancelled = true; };
    }, [eventId, getAuthHeader]);

    const selectedChannels = useMemo(
        () => Object.entries(channels).filter(([, on]) => on).map(([k]) => k),
        [channels],
    );
    const messageCount = selectedChannels.length * 3;

    const goStep = (n) => setStep(n);
    const toggleChannel = (key, on) => setChannels(c => ({ ...c, [key]: on }));
    const toggleLinkInclusion = (key, idx) => setLinkInclusion(cur => {
        const row = cur[key] || [false, false, false];
        const next = [...row];
        next[idx] = !next[idx];
        return { ...cur, [key]: next };
    });

    const generate = async () => {
        if (!eventId) { toast.error('Pick an event'); return; }
        if (!selectedChannels.length) { toast.error('Select at least one channel'); return; }
        setGenerating(true);
        try {
            // Create or reuse a session, then kick off generation.
            const token = localStorage.getItem('crm_token');
            const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
            const create = await axios.post(`${API}/event-outreach`, {
                contact_id: contactId, event_id: eventId, mode,
                context: contextText, include, channels: selectedChannels,
                link_inclusion: linkInclusion,
            }, { headers });
            const s = create.data.session;
            const gen = await axios.post(`${API}/event-outreach/${s.session_id}/generate`, null, { headers });
            setSession(gen.data.session);
            setActiveChannel(selectedChannels[0]);
            setStep(3);
            toast.success(`${messageCount} messages drafted`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Generation failed');
        } finally {
            setGenerating(false);
        }
    };

    const createTasks = async () => {
        if (!session) return;
        setCreatingTasks(true);
        try {
            const token = localStorage.getItem('crm_token');
            const r = await axios.post(
                `${API}/event-outreach/${session.session_id}/create-tasks`,
                null,
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            );
            setSession(r.data.session);
            toast.success(`${r.data.tasks_created} tasks created`);
            setStep(4);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create tasks');
        } finally {
            setCreatingTasks(false);
        }
    };

    const messagesByChannel = useMemo(() => {
        const grouped = {};
        (session?.messages || []).forEach(m => {
            (grouped[m.channel] = grouped[m.channel] || []).push(m);
        });
        Object.values(grouped).forEach(arr => arr.sort((a, b) => a.sequence_index - b.sequence_index));
        return grouped;
    }, [session]);

    if (!contact) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 animate-fade-in">
            {/* ─── Header ─── */}
            <div className="flex items-start gap-3">
                <button onClick={() => navigate(`/contacts/${contactId}`)} className="p-1 text-stone-500 hover:text-stone-900 -ml-1">
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center">
                            <Sparkles className="w-4 h-4" />
                        </div>
                        <h1 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Event Outreach Studio
                        </h1>
                        <span className="text-[10px] font-semibold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5 flex items-center gap-1">
                            <Sparkles className="w-2.5 h-2.5" /> AI assisted
                        </span>
                    </div>
                    <div className="text-xs text-stone-500 mt-1">
                        {mode === 'pre' ? 'Drive attendance and warm up before the meeting.' : 'Follow up after the meeting — recap + next step.'}
                        {' · '}
                        For <span className="font-semibold text-stone-700">{contact.first_name} {contact.last_name}</span>
                    </div>
                </div>
            </div>

            {/* Step 1: mode toggle + event grid */}
            {step === 1 && (
                <>
                    <ModeToggle mode={mode} onChange={(m) => { setMode(m); setEventId(''); }} />
                    <ProgressStepper step={1} />
                    <div className="bg-white border border-stone-200 rounded-xl p-4">
                        <div className="text-xs font-semibold text-stone-500 mb-3 uppercase tracking-wider">
                            {mode === 'pre' ? '1 · Event they are invited to' : '1 · Event they attended'}
                        </div>
                        {loadingEvents ? (
                            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></div>
                        ) : events.length === 0 ? (
                            <div className="py-10 text-center text-sm text-stone-400">
                                {mode === 'pre'
                                    ? 'No published events to invite this contact to. Publish an event first from /events.'
                                    : 'No attended events yet for this contact.'}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {events.map(e => (
                                    <EventCard key={e.event_id} event={e} selected={eventId === e.event_id} onSelect={setEventId} />
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end">
                        <button
                            onClick={() => goStep(2)}
                            disabled={!eventId}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-40"
                        >
                            Next: add context
                        </button>
                    </div>
                </>
            )}

            {/* Step 2: context + sources + channels */}
            {step === 2 && (
                <>
                    <ProgressStepper step={2} />
                    <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-4">
                        <div>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider">2 · Context for the AI</div>
                                <span className="text-[10px] text-stone-400">{contextText.length} chars</span>
                                {dictation.supported && (
                                    <button
                                        type="button"
                                        onClick={() => dictation.recording ? dictation.stop() : startDictation()}
                                        title={dictation.recording ? 'Stop dictation' : 'Dictate instead of typing'}
                                        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                                            dictation.recording
                                                ? 'bg-red-50 text-red-600 border-red-200 animate-pulse'
                                                : 'bg-white text-stone-600 border-stone-200 hover:border-violet-400 hover:text-violet-700'
                                        }`}
                                        data-action="outreach.dictate_context"
                                    >
                                        {dictation.recording
                                            ? <><MicOff className="w-3 h-3" /> Stop</>
                                            : <><Mic className="w-3 h-3" /> Dictate</>}
                                    </button>
                                )}
                                <span className="ml-auto text-[10px] text-stone-400 italic">Private to this contact</span>
                            </div>
                            <div className="text-xs text-stone-500 mb-2">
                                Describe how you met, what they said, anything you want the AI to use for ice-breakers. Adds on top of the general event record.
                                {dictation.supported && <span className="ml-1 text-stone-400">Type, or hit Dictate to speak it.</span>}
                            </div>
                            <div className="relative">
                                <textarea
                                    value={contextText}
                                    onChange={(e) => setContextText(e.target.value)}
                                    rows={5}
                                    placeholder="e.g. We met at the Q2 RevOps roundtable — they're evaluating…"
                                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 resize-vertical ${
                                        dictation.recording
                                            ? 'border-red-300 focus:ring-red-200 bg-red-50/20'
                                            : 'border-stone-200 focus:ring-violet-300'
                                    }`}
                                />
                                {dictation.recording && (
                                    <div className="absolute bottom-2 right-3 text-[10px] text-red-600 font-medium flex items-center gap-1 bg-white/90 px-2 py-0.5 rounded">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                        Listening…
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {[
                                { key: 'notes', Icon: FileText, title: 'Contact notes', sub: (contact.notes ? `${(contact.notes || '').split('\n').length} lines` : 'none on file') },
                                { key: 'disc',  Icon: Sparkles, title: 'DISC', sub: contact.disc_profile ? `Primary ${contact.disc_profile}` : 'not captured' },
                                { key: 'voice', Icon: FileText, title: 'Your voice', sub: `matt_voice.md · rep profile` },
                            ].map(src => (
                                <label key={src.key} className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${
                                    include[src.key] ? 'border-violet-400 bg-violet-50/40' : 'border-stone-200 bg-white'
                                }`}>
                                    <input
                                        type="checkbox"
                                        checked={include[src.key]}
                                        onChange={(e) => setInclude(i => ({ ...i, [src.key]: e.target.checked }))}
                                        className="mt-0.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 text-xs font-semibold text-stone-700">
                                            <src.Icon className="w-3 h-3" /> {src.title}
                                        </div>
                                        <div className="text-[10px] text-stone-500 mt-0.5">{src.sub}</div>
                                    </div>
                                </label>
                            ))}
                        </div>

                        {eventLinks.length > 0 && (
                            <div>
                                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                                    Event links — pick which messages they go in
                                </div>
                                <div className="text-[11px] text-stone-500 mb-2">
                                    Message 1 is a Connect-only message by default (no link). Flip the boxes for Msg 2 and Msg 3 as you like.
                                </div>
                                <div className="rounded-lg border border-stone-200 overflow-hidden">
                                    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-1 items-center bg-stone-50 border-b border-stone-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                                        <span>Link</span>
                                        <span className="w-12 text-center">Msg 1</span>
                                        <span className="w-12 text-center">Msg 2</span>
                                        <span className="w-12 text-center">Msg 3</span>
                                    </div>
                                    {eventLinks.map(link => {
                                        const row = linkInclusion[link.key] || [false, false, false];
                                        return (
                                            <div
                                                key={link.key}
                                                className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-1 items-center px-3 py-2 border-b border-stone-100 last:border-b-0"
                                            >
                                                <div className="min-w-0">
                                                    <div className="text-xs font-semibold text-stone-700">{link.label}</div>
                                                    <a
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-[10px] text-violet-700 hover:underline truncate block max-w-[260px]"
                                                        title={link.url}
                                                    >
                                                        {link.url}
                                                    </a>
                                                </div>
                                                {[0, 1, 2].map(idx => (
                                                    <label
                                                        key={idx}
                                                        className="w-12 flex justify-center items-center cursor-pointer"
                                                        title={`Include in message ${idx + 1}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(row[idx])}
                                                            onChange={() => toggleLinkInclusion(link.key, idx)}
                                                            className="w-4 h-4 accent-violet-600"
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Channels (pick any)</div>
                            <div className="flex flex-wrap gap-2">
                                {CHANNELS.map(c => (
                                    <ChannelToggle key={c.key} channel={c} checked={Boolean(channels[c.key])} onChange={toggleChannel} />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <button onClick={() => goStep(1)} className="text-xs text-stone-500 hover:text-stone-900">← Back</button>
                        <button
                            onClick={generate}
                            disabled={generating || !selectedChannels.length}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
                        >
                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            Generate {messageCount} messages
                        </button>
                    </div>
                </>
            )}

            {/* Step 3: review + personalise */}
            {step === 3 && session && (
                <>
                    <ProgressStepper step={3} />
                    <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-4">
                        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                            {selectedChannels.map(ck => {
                                const channel = CHANNELS.find(c => c.key === ck);
                                const count = (messagesByChannel[ck] || []).length;
                                const active = activeChannel === ck;
                                return (
                                    <button
                                        key={ck}
                                        onClick={() => setActiveChannel(ck)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                                            active ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                                        }`}
                                    >
                                        {channel && <channel.Icon className="w-3.5 h-3.5" />}
                                        {channel?.label}
                                        <span className={`ml-1 text-[10px] px-1 rounded ${active ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-500'}`}>{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="space-y-3">
                            {(messagesByChannel[activeChannel] || []).map(m => (
                                <MessageRow
                                    key={m.message_id}
                                    message={m}
                                    session={session}
                                    onChange={setSession}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <button onClick={() => goStep(2)} className="text-xs text-stone-500 hover:text-stone-900">← Back</button>
                        <button
                            onClick={createTasks}
                            disabled={creatingTasks}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
                        >
                            {creatingTasks ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            Create {messageCount} send tasks
                        </button>
                    </div>
                </>
            )}

            {/* Step 4: done */}
            {step === 4 && session && (
                <>
                    <ProgressStepper step={4} />
                    <div className="bg-white border border-stone-200 rounded-xl p-8 text-center space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                            <Check className="w-6 h-6" />
                        </div>
                        <h2 className="text-lg font-semibold text-stone-800">{messageCount} tasks created</h2>
                        <p className="text-sm text-stone-500 max-w-md mx-auto">
                            Each message is now a task on your list with its scheduled send-date. Work them in sequence — send, paste the reply, then rework the next message with the response for context.
                        </p>
                        <div className="flex items-center justify-center gap-2 pt-2">
                            <button
                                onClick={() => navigate(`/contacts/${contactId}`)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800"
                            >
                                Back to contact
                            </button>
                            <button
                                onClick={() => navigate('/tasks')}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-stone-600 hover:text-stone-900"
                            >
                                Go to my tasks
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
